/**
 * Leads data layer — where every page reads leads and changes their status.
 *
 * Reading: with REACT_APP_LEADS_URL set (and real sign-in), leads are the Zoho Inspection records,
 * loaded through n8n ("[C] App 1 - Get Inspections"): 7 days back → 3 weeks ahead, stages
 * Inspection Booked / Rebooking / Cancelled / Waiting for Report/Quote. Otherwise: test data.
 *
 * Writing: status changes apply here and queue for Zoho; they're wired to Zoho in the next step:
 *   stage            → the Deal's Stage field
 *   appointment      → the appointment start / end fields (reschedule)
 *   note             → a Note on the Deal ("Cancelled: <reason>")
 * Pages never touch Zoho directly, so connecting it doesn't change them.
 */
import { useSyncExternalStore } from 'react';
import mockInspections from '../mockData/mockInspections';
import { queueChange, sendInspectionUpdate } from './sync';
import { AUTH_PROVIDER, getAccessToken } from './auth';

const LEADS_URL = process.env.REACT_APP_LEADS_URL;
export const LEADS_SOURCE = LEADS_URL && AUTH_PROVIDER === 'supabase' ? 'zoho' : 'test';

let leads = LEADS_SOURCE === 'zoho' ? [] : mockInspections.map((l) => ({ ...l, statusHistory: l.statusHistory || [] }));
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

/* ---------- loading from Zoho ---------- */
// status: 'idle' | 'loading' | 'ready' | 'error'
let load = { status: LEADS_SOURCE === 'zoho' ? 'idle' : 'ready', error: '', fetchedAt: null };
const setLoad = (next) => {
  load = { ...load, ...next };
  emit();
};
export const useLeadsLoad = () => useSyncExternalStore(subscribeLeads, () => load);

// changes made in the app that aren't in Zoho yet — kept on top of each refresh until writing is wired
const localChanges = new Map();

let inFlight = null;
export const refreshLeads = () => {
  if (LEADS_SOURCE !== 'zoho') return Promise.resolve(leads);
  if (inFlight) return inFlight;
  setLoad({ status: 'loading', error: '' });
  inFlight = (async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Sign in again to load appointments.');
      const res = await fetch(LEADS_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `Couldn’t load appointments (${res.status}).`);
      const previous = new Map(leads.map((l) => [l.id, l]));
      leads = body.leads.map((l) => {
        const before = previous.get(l.id) || {};
        return { ...l, statusHistory: before.statusHistory || [], notes: before.notes || [], ...(localChanges.get(l.id) || {}) };
      });
      setLoad({ status: 'ready', error: '', fetchedAt: body.fetchedAt || new Date().toISOString(), count: leads.length });
    } catch (err) {
      setLoad({ status: 'error', error: err.message || 'Couldn’t load appointments.' });
    } finally {
      inFlight = null;
    }
    return leads;
  })();
  return inFlight;
};

export const getLeads = () => leads;
export const getLead = (id) => leads.find((l) => l.id === id) || null;

const subscribeLeads = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// re-render whenever any lead changes
export const useLeads = () => useSyncExternalStore(subscribeLeads, getLeads);
export const useLead = (id) => {
  const all = useLeads();
  return all.find((l) => l.id === id) || null;
};

/* ---------- booking rules (CLAUDE.md: 1-day min notice, 3-week max window, 2-hour appointments) ---------- */
export const APPOINTMENT_HOURS = 2;
export const MIN_NOTICE_DAYS = 1;
export const MAX_WINDOW_DAYS = 21;
// first and last start times of the day (24h) — 8 AM … 4 PM starts, so the last visit ends at 6 PM
export const FIRST_START_HOUR = 8;
export const LAST_START_HOUR = 16;

const pad = (n) => String(n).padStart(2, '0');
const localISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const bookableRange = (today = new Date()) => {
  const min = new Date(today);
  min.setDate(min.getDate() + MIN_NOTICE_DAYS);
  const max = new Date(today);
  max.setDate(max.getDate() + MAX_WINDOW_DAYS);
  return { min: localISODate(min), max: localISODate(max) };
};

export const startHours = () =>
  Array.from({ length: LAST_START_HOUR - FIRST_START_HOUR + 1 }, (_, i) => FIRST_START_HOUR + i);

const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`;
export const slotLabel = (h) => `${hourLabel(h)} – ${hourLabel(h + APPOINTMENT_HOURS)}`;

// Toronto offset for that date (EDT −04:00 / EST −05:00), so stored times match the rest of the data
const torontoOffset = (dateISO) => {
  const probe = new Date(`${dateISO}T12:00:00Z`);
  const tz = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Toronto', timeZoneName: 'shortOffset' })
    .formatToParts(probe)
    .find((p) => p.type === 'timeZoneName').value; // "GMT-4"
  const h = parseInt(tz.replace('GMT', ''), 10) || -5;
  return `${h < 0 ? '-' : '+'}${pad(Math.abs(h))}:00`;
};

export const appointmentFor = (dateISO, startHour) => {
  const off = torontoOffset(dateISO);
  return {
    appointmentStart: `${dateISO}T${pad(startHour)}:00:00${off}`,
    appointmentEnd: `${dateISO}T${pad(startHour + APPOINTMENT_HOURS)}:00:00${off}`,
  };
};

/* ---------- status changes ---------- */

/**
 * action: 'Cancelled' | 'Rebook' | 'Reschedule' | 'Report Sent'
 * note:   required for Cancelled / Rebook (optional for the others)
 * Reschedule: date + start (minutes, one of the booking windows 600/720/840/960) — or startHour in test mode
 * Report Sent: amount — the highest quote, when the agent's quotes are in the app (sets the Deal amount)
 *
 * With Zoho connected the change is saved in Zoho first: if Zoho refuses (the window was just taken,
 * no Deal linked…) nothing changes here and the reason is shown. No signal → applied here, queued,
 * and sent when the phone is back online.
 */
const ZOHO_ACTION = { Cancelled: 'cancel', Rebook: 'rebook', Reschedule: 'reschedule', 'Report Sent': 'report_sent' };

// save in Zoho now; fall back to the queue only when there's no connection
const toZoho = async ({ id, zoho, type, label, payload }) => {
  if (LEADS_SOURCE !== 'zoho') {
    queueChange({ type, leadId: id, label, payload });
    return { queued: false, saved: null };
  }
  try {
    const saved = await sendInspectionUpdate(zoho);
    queueChange({ type, leadId: id, label, payload, zoho, synced: true });
    return { queued: false, saved };
  } catch (err) {
    if (!err.offline) throw err;
    queueChange({ type, leadId: id, label, payload, zoho });
    return { queued: true, saved: null };
  }
};

export const updateLeadStatus = async (id, { action, note = '', date, startHour, start, amount }) => {
  const lead = getLead(id);
  if (!lead) throw new Error('Lead not found');
  const trimmed = note.trim();
  if ((action === 'Cancelled' || action === 'Rebook') && !trimmed) throw new Error('A note is required');

  const entry = { at: new Date().toISOString(), action, note: trimmed };
  let changes;
  if (action === 'Reschedule') {
    const minutes = start != null ? start : startHour != null ? startHour * 60 : null;
    if (!date || minutes == null) throw new Error('Pick a new date and time');
    const appt = appointmentFor(date, minutes / 60);
    entry.from = lead.appointmentStart;
    entry.to = appt.appointmentStart;
    changes = { ...appt, stage: 'Inspection Booked' };
  } else {
    changes = { stage: action };
  }

  const label =
    action === 'Reschedule'
      ? `${lead.contactName} — rescheduled to ${new Date(entry.to).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })}`
      : `${lead.contactName} — ${action}`;
  const minutes = action === 'Reschedule' ? (start != null ? start : startHour * 60) : undefined;
  const { queued, saved } = await toZoho({
    id,
    type: action === 'Reschedule' ? 'lead.reschedule' : 'lead.status',
    label,
    payload: { stage: changes.stage, note: trimmed, appointmentStart: changes.appointmentStart, appointmentEnd: changes.appointmentEnd, amount },
    zoho: { action: ZOHO_ACTION[action], inspectionId: id, note: trimmed, date, start: minutes, amount: amount == null ? null : Number(amount) },
  });

  // Zoho's own times win (it applies the Toronto offset for that date)
  if (saved && saved.appointmentStart) Object.assign(changes, { appointmentStart: saved.appointmentStart, appointmentEnd: saved.appointmentEnd });
  const updated = { ...lead, ...changes, statusHistory: [...(lead.statusHistory || []), { ...entry, queued }] };
  leads = leads.map((l) => (l.id === id ? updated : l));
  // keep it on top of refreshes only while it isn't in Zoho yet
  if (LEADS_SOURCE !== 'zoho' || queued) localChanges.set(id, { ...(localChanges.get(id) || {}), ...changes, statusHistory: updated.statusHistory });
  else localChanges.delete(id);
  emit();
  return updated;
};

/**
 * Admin: assign the visit to an agent (or change who has it). Saved in Zoho first — the Inspection's
 * Assigned_Agent + Agent_Name, and a booked visit's slot guard, so Zoho refuses an agent who already has
 * a visit at that time. No signal → applied here and sent later. agent = { id, name } (Zoho Agents).
 */
// fallback: a customer opened from search who isn't on this phone's route ({ contactName, agentName })
export const assignAgent = async (id, agent, fallback = {}) => {
  const onRoute = getLead(id);
  const lead = onRoute || { id, contactName: fallback.contactName || 'Customer', agentName: fallback.agentName || '' };
  if (!agent || !agent.id) throw new Error('Pick an agent');
  const entry = { at: new Date().toISOString(), action: 'Assigned', note: `Assigned to ${agent.name}${lead.agentName ? ` (was ${lead.agentName})` : ''}` };
  const { queued } = await toZoho({
    id,
    type: 'lead.assign',
    label: `${lead.contactName} — assigned to ${agent.name}`,
    payload: { agent: agent.name },
    zoho: { action: 'assign', inspectionId: id, agentId: agent.id, agentName: agent.name },
  });
  const changes = { agentName: agent.name, assignedAgent: agent.name };
  if (!onRoute) return { ...lead, ...changes };
  const updated = { ...lead, ...changes, statusHistory: [...(lead.statusHistory || []), { ...entry, queued }] };
  leads = leads.map((l) => (l.id === id ? updated : l));
  if (LEADS_SOURCE !== 'zoho' || queued) localChanges.set(id, { ...(localChanges.get(id) || {}), ...changes, statusHistory: updated.statusHistory });
  else localChanges.delete(id);
  emit();
  return updated;
};

/**
 * The reschedule picker: the agent's open 2-hour windows from the booking system (n8n
 * "[C] App 3 - Agent Free Windows") — the same answer the website gets. Zoho mode only.
 */
const WINDOWS_URL = process.env.REACT_APP_FREE_WINDOWS_URL;
export const RESCHEDULE_FROM_ZOHO = LEADS_SOURCE === 'zoho' && !!WINDOWS_URL;
export const getFreeWindows = async (id) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to see open times.');
  let res;
  try {
    res = await fetch(WINDOWS_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ inspectionId: id }) });
  } catch {
    throw new Error('No connection — open times need a signal.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || 'Couldn’t load open times.');
  return out; // { agent, source, days: [{ date, weekday, windows: [{ label, start, available }] }] }
};

/* ---------- after the inspection ---------- */

// the report + quotes went out — the visit is done, the lead moves on (and into Calls for follow-up)
export const markReportSent = (id, { quotes = 0 } = {}) => {
  const lead = getLead(id);
  if (!lead) return null;
  const entry = { at: new Date().toISOString(), action: 'Report Sent', note: `Inspection report + ${quotes} quote${quotes === 1 ? '' : 's'} sent.` };
  const updated = { ...lead, stage: 'Report Sent', statusHistory: [...(lead.statusHistory || []), entry] };
  leads = leads.map((l) => (l.id === id ? updated : l));
  localChanges.set(id, { ...(localChanges.get(id) || {}), stage: 'Report Sent', statusHistory: updated.statusHistory });
  emit();
  queueChange({ type: 'lead.status', leadId: id, label: `${lead.contactName} — Report Sent`, payload: { stage: 'Report Sent', note: entry.note } });
  return updated;
};

// agent notes on the lead page — a Note on the Zoho Inspection, signed by the login
export const addLeadNote = async (id, text, author) => {
  const lead = getLead(id);
  const clean = (text || '').trim();
  if (!lead || !clean) return null;
  await toZoho({
    id,
    type: 'lead.note',
    label: `${lead.contactName} — note added`,
    payload: { note: clean },
    zoho: { action: 'note', inspectionId: id, note: clean },
  });
  const note = { id: `ln-${Date.now()}`, at: new Date().toISOString(), author, text: clean };
  const updated = { ...lead, notes: [...(lead.notes || []), note] };
  leads = leads.map((l) => (l.id === id ? updated : l));
  emit();
  return updated;
};
